import { queryKeys } from '@query/keys'
import { PaginatedQueryResult, usePaginatedQuery } from '@query/usePaginatedQuery'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionLanguage } from '@app/context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { FilterQueryProps } from '@model/FilterFields'
import { ReferralMaterialFilterOptionsDto } from '@model/ReferralMaterialDto'

interface ReferralMaterials extends ReferralMaterialFilterOptionsDto {
  query: PaginatedQueryResult<import('@model/ReferralMaterialDto').ReferralMaterialDto>
  search: FilterQueryProps
  setSearch: (search: FilterQueryProps) => void
  clearSearch: (callback: () => void) => void
  isLoading: boolean
  forceRefresh: () => void
}

const defaultLanguage = 'en'

export const useReferralMaterialsWithFilters = (): ReferralMaterials => {
  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const domain = window.location.host
  const { t } = useTranslation()

  const [search, setSearch] = useState<FilterQueryProps>({})

  const { data: filterData, isLoading: isFiltersLoading } = useQuery({
    queryKey: queryKeys.ib.referralMaterialFilterOptions(locale),
    queryFn: async () => {
      const options = await apiClient.getReferralMaterialFilterOptions(locale, domain)

      if (!options?.languages?.length) {
        return apiClient.getReferralMaterialFilterOptions(defaultLanguage, domain)
      }

      const translatedCategories = options?.categories.map((category) => {
        return { id: category.id, name: t(`Referral Materials.${category.name}`) }
      })

      return { ...options, categories: translatedCategories }
    },
    staleTime: 5 * 60_000,
  })

  const query = usePaginatedQuery({
    queryKey: queryKeys.ib.referralMaterials(locale, search as Record<string, unknown>),
    queryFn: (pageQuery) =>
      apiClient.getReferralMaterials(locale, {
        ...pageQuery,
        search,
        caller: 'ca',
        isPageSizeDivisibleByThree: true,
      }),
  })

  const clearSearch = (callback: () => void) => {
    callback()
    setSearch({})
  }

  return {
    query,
    campaigns: filterData?.campaigns || [],
    categories: filterData?.categories || [],
    languages: filterData?.languages || [],
    sizes: filterData?.sizes || [],
    search,
    setSearch,
    clearSearch,
    isLoading: query.isLoading || isFiltersLoading,
    forceRefresh: query.forceRefresh,
  }
}
